import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon, EyeIcon } from "@heroicons/react/24/outline";
import { Container, EmptyState, Loading, PageHeader, RowAction, Select, Table, Tag } from "../../ui";
import { formatDuration, placeOf, kindOf, needsSponsor, useStaffScholarship } from "../model";
import ApplicationDialog from "./ApplicationDialog";
import { applicationStateOf, applicationStatesFor, formatDateTime, formatScore, fullName, useScholarshipApplications } from "./model";
import { ApplicantName } from "./EligibilityFlag";

const PAGE_SIZE = 20;

// The applicants for one programme of a scholarship; a row opens the application in full
export default function ProgrammeApplicantsPage() {
  const { scholarshipId, programmeId } = useParams();
  const [state, setState] = useState("");
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState(null);
  const { data: scholarshipResult, isPending: loadingScholarship, isError: scholarshipFailed, refetch } = useStaffScholarship(scholarshipId);
  const { data, isPending, isError } = useScholarshipApplications(scholarshipId, { programmeId, state, page, size: PAGE_SIZE });

  const scholarship = scholarshipResult?.data;
  const programme = scholarship?.programmes.find((p) => p.id === programmeId);

  if (loadingScholarship) return <Loading label="Loading the programme…" />;
  if (scholarshipFailed || !programme) return <EmptyState title="Could not load this programme" description="It may have been removed, or you may not have access." onRefresh={refetch} />;

  const open = (row) => setOpenId(row.id);

  const columns = [
    { key: "applicant", label: "Applicant", accessor: (r) => fullName(r.applicant), render: (r) => <ApplicantName name={fullName(r.applicant)} meets={r.meetsEligibility} /> },
    { key: "email", label: "Email", accessor: (r) => r.applicant.email },
    { key: "phone", label: "Phone", accessor: (r) => r.applicant.phoneNumber ?? "—" },
    { key: "submittedAt", label: "Submitted", accessor: (r) => formatDateTime(r.submittedAt), sortValue: (r) => r.submittedAt },
    { key: "marked", label: "Marked", accessor: (r) => `${r.evaluationsSubmitted} / ${r.evaluationsExpected}` },
    { key: "meanScore", label: "Score", accessor: (r) => formatScore(r.meanScore), sortValue: (r) => r.meanScore },
    {
      key: "state",
      label: "State",
      accessor: (r) => applicationStateOf(r.state).label,
      render: (r) => <Tag color={applicationStateOf(r.state).color}>{applicationStateOf(r.state).label}</Tag>,
    },
    {
      key: "open",
      label: "",
      exportable: false,
      render: (r) => (
        <RowAction Icon={EyeIcon} label="View" onClick={() => open(r)} />
      ),
    },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={`${kindOf(scholarship.type).applicantsPath}/${scholarshipId}`} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> {scholarship.name}
      </Link>
      <PageHeader
        title={programme.name}
        subtitle={`${[placeOf(programme), programme.level?.name ?? "—"].filter(Boolean).join(" · ")} · ${formatDuration(programme.durationMonths)}`}
      />
      <Table cardView viewKey="programme-applicants"
        columns={columns}
        data={data?.content ?? []}
        onRowClick={open}
        isLoading={isPending}
        searchable={false}
        exportName={`applicants-${programme.name}`}
        emptyTitle={isError ? "Could not load the applicants. Please try again later." : "No applicants for this programme yet"}
        pagination={{ page, totalPages: data?.totalPages ?? 0, totalElements: data?.totalElements, pageSize: PAGE_SIZE, onPageChange: setPage }}
        filters={
          <Select
            aria-label="State"
            placeholder="All states"
            value={state}
            onChange={(e) => {
              setState(e.target.value);
              setPage(0);
            }}
            options={applicationStatesFor(scholarship.type)}
            className="w-full sm:w-48"
          />
        }
      />
      <ApplicationDialog key={openId} applicationId={openId} programmes={scholarship.programmes} sponsored={needsSponsor(scholarship.type)} onClose={() => setOpenId(null)} />
    </Container>
  );
}
